import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";
import { ProtectedRoute } from "@/lib/protected-route";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
import Profile from "@/pages/profile";
import ProfileSettings from "@/pages/profile-settings";
import ResetPassword from "@/pages/reset-password";
import AdminUsers from "@/pages/admin-users";
import Clubhouse from "@/pages/clubhouse";
import AdminBookings from "@/pages/admin-bookings";
import AdminGofundme from "@/pages/admin-gofundme";
import AdminSMS from "@/pages/admin-sms";
import AdminSetDetails from "@/pages/admin/set-details";
import ManageCalendarPage from "@/pages/manage-calendar";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import _ from "lodash";
import MyBookings from "@/pages/my-bookings"; // Import the new component
import VideoReviewPage from "@/pages/VideoReviewPage"; // Cascade: Import VideoReviewPage
import LandingPage from "@/pages/landing";
import NewBookingPage from "@/pages/new-booking";
import EditBookingPage from "@/pages/edit-booking";
import { NotificationPreferencesPage } from "@/pages/notification-preferences";
import PrivateRecordingsPage from "@/pages/admin/private-recordings";
import PrivateRecordingDetailPage from "@/pages/admin/private-recording-detail";
import PrivateRecordingViewerPage from "@/pages/admin/private-recording-viewer";
import SetViewerPage from "@/pages/set-viewer";
import { useVersionCheck } from "@/hooks/use-version-check";
import { initializeSentry, setSentryUser } from "@/lib/sentry";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Initialize Sentry once at module load
initializeSentry();

function AdminRoute({
  path,
  component: Component,
}: {
  path: string;
  component: () => React.JSX.Element;
}) {
  const { user, isLoading } = useAuth();
  const roles = _.get(user, "roles", []);
  const isAdmin = _.some(
    roles,
    (role: { name: string }) => role?.name === "admin",
  );

  // console.log("isAdmin -->", isAdmin);

  if (isLoading) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-border" />
        </div>
      </Route>
    );
  }

  if (!user || !isAdmin) {
    return (
      <Route path={path}>
        <Redirect to="/" />
      </Route>
    );
  }

  return <Route path={path} component={Component} />;
}


function App() {
  // Check for app updates
  useVersionCheck();
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SentryUserContext />
          <Switch>
            <Route path="/recording/:token" component={PrivateRecordingViewerPage} />
            <Route path="/set/:token" component={SetViewerPage} />
            <Route path="/login" component={AuthPage} />
            <Route path="/reset-password" component={ResetPassword} />
            <ProtectedRoute path="/profile" component={Profile} />
            <ProtectedRoute
              path="/profile/settings"
              component={ProfileSettings}
            />
            <ProtectedRoute path="/my-bookings" component={MyBookings} /> {/* Added route */}
            <ProtectedRoute path="/review/:bookingId" component={VideoReviewPage} /> {/* Cascade: Added VideoReviewPage route */}
            <ProtectedRoute path="/booking/new" component={NewBookingPage} />
            <ProtectedRoute path="/booking/:bookingId/edit" component={EditBookingPage} />
            <ProtectedRoute path="/profile/notifications" component={NotificationPreferencesPage} />
            <AdminRoute path="/admin/users" component={AdminUsers} />
            <AdminRoute path="/admin/bookings" component={AdminBookings} />
            <AdminRoute path="/admin/gofundme" component={AdminGofundme} />
            <AdminRoute path="/admin/sms" component={AdminSMS} />
            <AdminRoute path="/admin/set-details/:bookingId" component={AdminSetDetails} />
            <AdminRoute path="/admin/calendar" component={ManageCalendarPage} />
            <AdminRoute path="/admin/private-recordings" component={PrivateRecordingsPage} />
            <AdminRoute path="/admin/private-recordings/:id" component={PrivateRecordingDetailPage} />
            <ProtectedRoute path="/" component={LandingPage} />
            <Route component={NotFound} />
          </Switch>
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Component to set Sentry user context when user changes
function SentryUserContext() {
  const { user } = useAuth();
  
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        displayName: user.displayName || undefined,
      });
    }
  }, [user]);
  
  return null;
}

export default App;